import { ActionIcon, Group, PasswordInput, type PasswordInputProps } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconEye, IconEyeOff } from '@tabler/icons-react';
import { ModalError } from '../..';
import { useEditorContext } from '../EditorContext';
import { ModalEditorItemBase } from './ModalEditorItemBase';

export interface ModalPasswordItemProps<TFormType> extends PasswordInputProps {
    fieldKey: keyof TFormType;
    required?: boolean;
    label?: string;
    labelWidth?: number;
}

export const ModalPasswordItem = <TFormType,>({
    fieldKey,
    required,
    size = 'xs',
    label,
    labelWidth,
    ...passwordInputProps
}: ModalPasswordItemProps<TFormType>) => {
    const { form, loading, onSave } = useEditorContext<TFormType>();
    const [visible, { toggle }] = useDisclosure(false);

    const fieldKeyString = fieldKey as string;
    const error = form.errors[fieldKeyString];

    const rightSection = (
        <Group gap={4} p={0} m={0} wrap="nowrap">
            <ModalError error={error} />
            {visible ? (
                <ActionIcon variant="transparent">
                    <IconEyeOff size="16" onClick={toggle} />
                </ActionIcon>
            ) : (
                <ActionIcon variant="transparent">
                    <IconEye size="16" onClick={toggle} />
                </ActionIcon>
            )}
        </Group>
    );

    return (
        <ModalEditorItemBase
            label={label}
            labelWidth={labelWidth}
            error={error}
            required={required}
            submitChanges={onSave}
        >
            <PasswordInput
                key={form.key(fieldKeyString)}
                {...form.getInputProps(fieldKeyString)}
                readOnly={loading}
                required={required}
                error={false}
                disabled={loading}
                visible={visible}
                onVisibilityChange={toggle}
                leftSectionPointerEvents="none"
                rightSection={rightSection}
                rightSectionWidth={error ? 64 : 36}
                size={size}
                {...passwordInputProps}
            />
        </ModalEditorItemBase>
    );
};
